/**
 * 接続線の「線区愛称」を、選択した線区のマスタ名称から自動入力するための純関数。
 *
 * ## 背景
 *
 * 線区愛称 (`route_nickname` / `route_nickname_i18n`) は、放送 SSML の
 * `{route_name}` / `{route}` に **最優先** で使われる。愛称が空だと運行データ
 * (Transformer) の線名に依存し、画面から実際に流れる線名が分からなかった。
 * そこで接続線の愛称を必須にし、線区を選ぶとマスタの名称
 * (`routes-master.json` の `route_name` / `route_name_i18n`) を初期値として入れる。
 *
 * ## 上書きの規則
 *
 * 手入力した愛称 (例: 「湘南新宿ライン」) を消さないために、書き換えるのは次のときだけ。
 *
 *   1. 空欄のとき (放送言語に含まれる言語のみ)
 *   2. 直前の線区のマスタ名称と **同じ** とき (= 自動入力のままと判断して追従する)
 *
 * マスタに無い線区 / マスタに外国語名が無い言語は、何も入れない (手入力する)。
 */

import type { BroadcastContent, ConnectingLineEntry } from '../api/types';
import type { RouteMaster } from '../data/routes';
import { DEFAULT_LANGUAGE_KEY } from '../data/pollyVoices';
import { isConnectingStationOtherMode } from './broadcastContents';
import type { LocalizedText } from './localizedSsml';

/** 愛称の 1 組 (日本語 + 言語別)。 */
export interface RouteNicknameValue {
  /** 日本語の愛称 (`route_nickname`)。未設定は空文字。 */
  route_nickname: string;
  /** 言語別の愛称 (`route_nickname_i18n`)。1 件も無ければ undefined。 */
  route_nickname_i18n: LocalizedText | undefined;
}

function clean(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 線区マスタから、指定言語の名称を引く。
 * 日本語は `route_name`、それ以外は `route_name_i18n[言語]`。無ければ空文字。
 */
export function masterRouteName(
  routes: readonly RouteMaster[],
  routeId: string | undefined,
  languageKey: string,
): string {
  const id = clean(routeId);
  if (id === '') return '';
  const route = routes.find((r) => r.route_id === id);
  if (!route) return '';
  if (languageKey === DEFAULT_LANGUAGE_KEY) return clean(route.route_name);
  return clean(route.route_name_i18n?.[languageKey]);
}

/**
 * 愛称を、線区の変更 (または初期表示) に合わせて同期する。
 *
 * @param prevRouteId 変更前のメイン route_id。初期表示 / 言語追加のときは `nextRouteId` と同じ値を渡す
 * @param nextRouteId 変更後のメイン route_id
 * @param current 現在の愛称
 * @param routes 線区マスタ
 * @param languages 放送言語 (空なら日本語のみ)
 * @returns 同期後の愛称。変更が無いときは `current` と同じ内容 (参照は別)
 */
export function syncRouteNickname(
  prevRouteId: string | undefined,
  nextRouteId: string | undefined,
  current: { route_nickname?: unknown; route_nickname_i18n?: LocalizedText | undefined },
  routes: readonly RouteMaster[],
  languages: readonly string[],
): RouteNicknameValue {
  const selected = languages.length > 0 ? languages : [DEFAULT_LANGUAGE_KEY];
  const dict: LocalizedText =
    current.route_nickname_i18n && typeof current.route_nickname_i18n === 'object'
      ? current.route_nickname_i18n
      : {};

  // --- 日本語 ---
  // 放送側は `route_nickname_i18n["ja"]` を `route_nickname` より優先する。
  // 言語別の日本語が入っている (旧データ / JSON インポート) ときは触らない。
  let nickname = typeof current.route_nickname === 'string' ? current.route_nickname : '';
  if (clean(dict[DEFAULT_LANGUAGE_KEY]) === '') {
    const cur = clean(nickname);
    const prevAuto = masterRouteName(routes, prevRouteId, DEFAULT_LANGUAGE_KEY);
    if (cur === '' || (prevAuto !== '' && cur === prevAuto)) {
      nickname = masterRouteName(routes, nextRouteId, DEFAULT_LANGUAGE_KEY);
    }
  }

  // --- 外国語 ---
  // 放送言語 + 既に値のある言語を対象にする。放送言語に無い言語は空欄を埋めない
  // (使わない言語の値を勝手に作らない)。値があれば、自動入力のままのとき追従する。
  const next: LocalizedText = {};
  const keys = new Set<string>([
    ...selected.filter((k) => k !== DEFAULT_LANGUAGE_KEY),
    ...Object.keys(dict).filter((k) => k !== DEFAULT_LANGUAGE_KEY),
  ]);
  for (const key of keys) {
    const cur = clean(dict[key]);
    const prevAuto = masterRouteName(routes, prevRouteId, key);
    const replace =
      (cur === '' && selected.includes(key)) || (cur !== '' && prevAuto !== '' && cur === prevAuto);
    const value = replace ? masterRouteName(routes, nextRouteId, key) : (dict[key] ?? '');
    if (clean(value) !== '') next[key] = value;
  }
  // 日本語の言語別名称 (上記で触らない) はそのまま引き継ぐ。
  if (typeof dict[DEFAULT_LANGUAGE_KEY] === 'string' && dict[DEFAULT_LANGUAGE_KEY] !== '') {
    next[DEFAULT_LANGUAGE_KEY] = dict[DEFAULT_LANGUAGE_KEY];
  }

  return {
    route_nickname: nickname,
    route_nickname_i18n: Object.keys(next).length > 0 ? next : undefined,
  };
}

/** 2 つの愛称が同じ内容か。 */
function sameNickname(
  a: RouteNicknameValue,
  current: { route_nickname?: unknown; route_nickname_i18n?: LocalizedText | undefined },
): boolean {
  const currentNickname = typeof current.route_nickname === 'string' ? current.route_nickname : '';
  if (a.route_nickname !== currentNickname) return false;
  const left = a.route_nickname_i18n ?? {};
  const right = current.route_nickname_i18n ?? {};
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if ((left[key] ?? '') !== (right[key] ?? '')) return false;
  }
  return true;
}

/**
 * 放送コンテンツ 1 件の接続線愛称の **空欄を** マスタの名称で埋める。
 *
 * 対象は接続線遅延 (part1) と接続線遅延 (乗換駅別 / part2) の各接続線。
 * 「その他」モード (運行情報のみを放送する) は愛称を使わないので触らない。
 * 変更が無ければ渡された `content` をそのまま返す (再描画を避ける)。
 */
export function fillContentRouteNicknames(
  content: BroadcastContent,
  routes: readonly RouteMaster[],
): BroadcastContent {
  const languages = content.languages ?? [DEFAULT_LANGUAGE_KEY];

  if (content.category === 'connecting_line_delay') {
    const props = content.properties as {
      route_id?: string;
      route_nickname?: string;
      route_nickname_i18n?: LocalizedText;
    };
    const synced = syncRouteNickname(props.route_id, props.route_id, props, routes, languages);
    if (sameNickname(synced, props)) return content;
    return {
      ...content,
      properties: {
        ...(content.properties as object),
        route_nickname: synced.route_nickname,
        route_nickname_i18n: synced.route_nickname_i18n,
      } as BroadcastContent['properties'],
    };
  }

  if (content.category === 'connecting_line_delay_by_station') {
    const props = content.properties as {
      transfer_stop_mode?: string;
      lines?: ConnectingLineEntry[];
    };
    if (isConnectingStationOtherMode(props as never)) return content;
    if (!Array.isArray(props.lines)) return content;
    let changed = false;
    const lines = props.lines.map((line) => {
      if (!line || typeof line !== 'object') return line;
      const synced = syncRouteNickname(line.route_id, line.route_id, line, routes, languages);
      if (sameNickname(synced, line)) return line;
      changed = true;
      return {
        ...line,
        route_nickname: synced.route_nickname,
        route_nickname_i18n: synced.route_nickname_i18n,
      };
    });
    if (!changed) return content;
    return {
      ...content,
      properties: { ...(content.properties as object), lines } as BroadcastContent['properties'],
    };
  }

  return content;
}

/**
 * 放送コンテンツ一覧の接続線愛称の空欄を埋める。
 * 変更が無ければ渡された配列をそのまま返す。
 */
export function fillContentsRouteNicknames(
  contents: readonly BroadcastContent[],
  routes: readonly RouteMaster[] | undefined,
): BroadcastContent[] {
  const list = contents as BroadcastContent[];
  if (!routes || routes.length === 0) return list;
  let changed = false;
  const next = list.map((content) => {
    const filled = fillContentRouteNicknames(content, routes);
    if (filled !== content) changed = true;
    return filled;
  });
  return changed ? next : list;
}
